import React, { useState, useEffect } from "react";
import { IconButton } from "../../UIElements/Button";
import { Icon } from "../../UIElements/Icon";
import { useGlobalUtils } from "../../../../lib/globalUtils";
import { useTranslation } from "react-i18next";

/**
 * OrderNotificationToast Component
 * Displays real-time order notifications with smooth animations
 */
const OrderNotificationToast = ({
  order,
  isVisible,
  onClose,
  duration = 5000,
  position = "top-right",
  type = "new",
}) => {
  const { t } = useTranslation("orders");
  const { parsePrice } = useGlobalUtils();

  const [isAnimating, setIsAnimating] = useState(false);
  const [timeLeft, setTimeLeft] = useState(duration / 1000);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      setTimeLeft(duration / 1000);

      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      // Update countdown every 100ms for smooth animation
      const countdownInterval = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 0.1;
          return newTime <= 0 ? 0 : newTime;
        });
      }, 100);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
  }, [isVisible, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  if (!isVisible || !order) return null;

  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
  };

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={`fixed ${positionClasses[position]} transform transition-all duration-300 ease-in-out motion-reduce:transform-none motion-reduce:transition-none ${
        isAnimating
          ? "translate-x-0 scale-100 opacity-100"
          : "translate-x-full scale-95 opacity-0"
      }`}
      style={{ zIndex: "var(--layer-toast)" }}
    >
      <div className="relative w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-[var(--od-radius-component)] border border-[var(--od-ink-600)] bg-[var(--od-ink-900)] text-[var(--od-white)] shadow-[var(--od-shadow-2)]">
        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-[var(--od-ink-600)] p-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--od-radius-component)] bg-[var(--od-blue-600)]">
              <Icon
                name="ClockArrowDown"
                className="relative h-4 w-4 text-[var(--od-white)]"
              />
            </div>
            <div className="min-w-0">
              <h4 className="truncate text-sm font-semibold text-[var(--od-white)]">
                {type === "new"
                  ? t("notifications.newOrder")
                  : t("notifications.orderUpdated")}{" "}
                #{order?.id}
              </h4>
            </div>
          </div>

          <IconButton
            type="button"
            variant="ghost"
            size="iconTouch"
            onClick={handleClose}
            aria-label={t("detail.actions.close")}
            className="group relative -me-2 shrink-0 rounded-[var(--od-radius-control)] text-[var(--od-ink-300)] hover:bg-[var(--od-ink-800)] hover:text-[var(--od-white)] focus-visible:ring-[var(--od-blue-600)] focus-visible:ring-offset-[var(--od-ink-900)] motion-reduce:transition-none"
          >
            <Icon
              name="X"
              className="h-4 w-4 transition-colors motion-reduce:transition-none"
            />
          </IconButton>
        </div>

        {/* Content */}
        <div className="relative p-3">
          <div className="space-y-2.5">
            {/* Order Details */}
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-[var(--od-radius-control)] border border-[var(--od-ink-600)] bg-[var(--od-ink-800)] px-2.5 py-2">
                <span className="text-xs text-[var(--od-ink-300)]">
                  {t("notifications.total")}
                </span>
                <span className="font-mono text-sm font-semibold text-[var(--od-white)] tabular-nums">
                  {parsePrice(order.summary?.total ?? 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Progress Bar */}
        <div
          aria-hidden="true"
          className="absolute start-0 end-0 bottom-0 h-1 overflow-hidden bg-[var(--od-ink-700)]"
        >
          <div
            className="absolute start-0 top-0 h-full bg-[var(--od-blue-600)] transition-all duration-100 ease-linear motion-reduce:transition-none"
            style={{
              width: `${(timeLeft / (duration / 1000)) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderNotificationToast;
